import React from 'react'
import { createContext } from 'lib/createContext'
import { Loading, Message } from '@workwhile/ui'
import { parseErrorMessage } from 'api'
import { useShiftsQuery } from 'queries/shifts/useShiftsQuery'
import { format } from 'date-fns'
import { useCompaniesQuery } from 'queries/companies'
import { useCompanyGroupsQuery } from 'queries/companyGroups'
import { useTimeRangeQueryState } from '../hooks/useTimeRangeQueryState'
import { usePaginationQueryState } from 'lib/nuqs/usePaginationQueryState'
import { useShiftQueryState } from '../hooks/useShiftQueryState'
import { useCompanyQueryState } from '../hooks/useCompanyQueryState'
import { SelectableColumns } from '../components/ShiftsTable/ColumnsFilter'
import { useSelectedColumnsQueryState } from '../hooks/useSelectedColumnsQueryState'
import { useMarketsQuery } from 'queries/markets'
import { useUsersQuery } from 'queries/users'
import { NotSetAccountOrSalesOwnerFilterId } from 'api/shifts/getAllShifts'

type ShiftsContextState = {
  state: {
    data: NonNullable<ReturnType<typeof useShiftsQuery>['data']>
    dataUpdatedAt: number
    isFetching: boolean
    isError: boolean
    error: string
    companies: NonNullable<ReturnType<typeof useCompaniesQuery>['data']>
    companyGroups: NonNullable<ReturnType<typeof useCompanyGroupsQuery>['data']>
    salesOwners: NonNullable<ReturnType<typeof useUsersQuery>['data']>
    accountOwners: NonNullable<ReturnType<typeof useUsersQuery>['data']>
    pagination: {
      pageIndex: number
      pageSize: number
    }
    timeRangeFilters: {
      type?: 'past' | 'ongoing' | 'upcoming'
      startDate?: Date
      endDate?: Date
    }
    shiftFilters: {
      shiftIds: number[]
      listingIds: number[]
      locationIds: number[]
      marketCodes: string[]
      scheduleRateMin: number | null
      scheduleRateMax: number | null
      fillRateMin: number | null
      fillRateMax: number | null
      shiftStatuses: ('active' | 'cancelled')[]
      positionIds: number[]
      rosterRestrictions: ('permanent' | 'temporary' | 'non-roster')[]
      isStandby: boolean | null
    }
    companyFilters: {
      companyIds: number[]
      companyGroupIds: number[]
      salesOwnerIds: (typeof NotSetAccountOrSalesOwnerFilterId | number)[]
      accountOwnerIds: (typeof NotSetAccountOrSalesOwnerFilterId | number)[]
      companyTypes: ('1099' | 'w2')[]
    }
    selectedColumnsFilters: SelectableColumns[]
    markets: ReturnType<typeof useMarketsQuery>['data']
  }
  actions: {
    refetch: () => void
    setPagination: ReturnType<typeof usePaginationQueryState>[1]
    setTimeRangeFilters: ReturnType<typeof useTimeRangeQueryState>[1]
    setShiftFilters: ReturnType<typeof useShiftQueryState>[1]
    setCompanyFilters: ReturnType<typeof useCompanyQueryState>[1]
    setSelectedColumnsFilters: ReturnType<
      typeof useSelectedColumnsQueryState
    >[1]
  }
}

const [Context, useShiftsContextValue] = createContext<ShiftsContextState>({
  name: 'ShiftsContext',
})

export const ShiftsProvider = ({ children }: { children: React.ReactNode }) => {
  const [pagination, setPagination] = usePaginationQueryState({
    defaultPageSize: 10,
  })
  const [timeRangeFilters, setTimeRangeFilters] = useTimeRangeQueryState()
  const [shiftFilters, setShiftFilters] = useShiftQueryState()
  const [companyFilters, setCompanyFilters] = useCompanyQueryState()
  const [selectedColumnsFilters, setSelectedColumnsFilters] =
    useSelectedColumnsQueryState()

  const {
    data,
    isPending: isShiftsLoading,
    isError: isShiftsError,
    error: shiftsError,
    dataUpdatedAt,
    refetch: refetchShifts,
    isFetching,
  } = useShiftsQuery({
    rangeType: timeRangeFilters.type,
    startDate: timeRangeFilters.startDate
      ? format(timeRangeFilters.startDate, 'yyyy-MM-dd')
      : undefined,
    endDate: timeRangeFilters.endDate
      ? format(timeRangeFilters.endDate, 'yyyy-MM-dd')
      : undefined,
    page: pagination.pageIndex,
    pageSize: pagination.pageSize,
    shiftIds: shiftFilters.shiftIds,
    listingIds: shiftFilters.listingIds,
    locationIds: shiftFilters.locationIds,
    marketCodes: shiftFilters.marketCodes,
    companyIds: companyFilters.companyIds,
    companyGroupIds: companyFilters.companyGroupIds,
    scheduleRateMin: shiftFilters.scheduleRateMin ?? undefined,
    scheduleRateMax: shiftFilters.scheduleRateMax ?? undefined,
    fillRateMin: shiftFilters.fillRateMin ?? undefined,
    fillRateMax: shiftFilters.fillRateMax ?? undefined,
    shiftStatuses: shiftFilters.shiftStatuses,
    salesOwnerIds: companyFilters.salesOwnerIds,
    accountOwnerIds: companyFilters.accountOwnerIds,
    companyTypes: companyFilters.companyTypes,
    positionIds: shiftFilters.positionIds,
    rosterRestrictions: shiftFilters.rosterRestrictions,
    isStandby: shiftFilters.isStandby ?? undefined,
  })

  const {
    data: companies,
    isError: isCompaniesError,
    error: companiesError,
    refetch: refetchCompanies,
  } = useCompaniesQuery()

  const {
    data: companyGroups,
    isError: isCompanyGroupsError,
    error: companyGroupsError,
    refetch: refetchCompanyGroups,
  } = useCompanyGroupsQuery()

  const {
    data: markets,
    isError: isMarketsError,
    error: marketsError,
    refetch: refetchMarkets,
  } = useMarketsQuery()

  const {
    data: salesOwners,
    isError: isSalesOwnersError,
    error: salesOwnersError,
    refetch: refetchSalesOwners,
  } = useUsersQuery({
    type: 'internal',
    statuses: ['active', 'account_created'],
    roles: ['sales_owner'],
  })

  const {
    data: accountOwners,
    isError: isAccountOwnersError,
    error: accountOwnersError,
    refetch: refetchAccountOwners,
  } = useUsersQuery({
    type: 'internal',
    statuses: ['active', 'account_created'],
    roles: ['account_owner'],
  })

  const isAnyError =
    isShiftsError ||
    isCompaniesError ||
    isCompanyGroupsError ||
    isMarketsError ||
    isSalesOwnersError ||
    isAccountOwnersError

  const error =
    shiftsError ||
    companiesError ||
    companyGroupsError ||
    marketsError ||
    salesOwnersError ||
    accountOwnersError

  // state.isError/state.error reflect all 6 queries, so retry needs to
  // recover all of them - not just the shifts list itself.
  const refetch = () => {
    refetchShifts()
    refetchCompanies()
    refetchCompanyGroups()
    refetchMarkets()
    refetchSalesOwners()
    refetchAccountOwners()
  }

  // Only the shifts query gates the full-page spinner. Gating on isAnyLoading
  // would re-trigger it whenever a lookup query with no cached data (e.g. a
  // previously-failed one) is refetched, since TanStack Query resets that
  // query's status to "pending" while it has no data - wiping the already
  // -rendered table on every retry instead of just refreshing the filters.
  if (isShiftsLoading) {
    return <Loading />
  }

  if (isShiftsError && !data) {
    // eslint-disable-next-line no-console
    console.error(shiftsError)
    return (
      <Message variant="error" m={4} borderRadius="small">
        Sorry, something went wrong. Please reach out to eng support with the
        URL of the page if this persists.
      </Message>
    )
  }

  if (!data) {
    return null
  }

  const value = {
    state: {
      data,
      dataUpdatedAt,
      isFetching,
      isError: isAnyError,
      error: parseErrorMessage(error),
      companies: companies ?? [],
      companyGroups: companyGroups ?? [],
      salesOwners: salesOwners ?? [],
      accountOwners: accountOwners ?? [],
      markets: markets ?? [],
      pagination,
      timeRangeFilters,
      shiftFilters,
      companyFilters,
      selectedColumnsFilters,
    },
    actions: {
      refetch,
      setPagination,
      setTimeRangeFilters,
      setShiftFilters,
      setCompanyFilters,
      setSelectedColumnsFilters,
    },
  }

  return <Context.Provider value={value}>{children}</Context.Provider>
}

export { useShiftsContextValue }
